import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Form851TaxualinfoService } from './form851-taxualinfo.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-form851-taxualinfo',
    templateUrl: './form851-taxualinfo.component.html',
    styleUrls: ['./form851-taxualinfo.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Form851TaxualinfoComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private lastContextKey = '';

  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  userSettings: any = {};
  crudLoading = false;

  loadingData = true;
  showLoader = true;

  tbl_list: any[] = [];
  sublist: any[] = [];
  sublist1: any[] = [];

  private isInitialized = false;
  private isLoading = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: Form851TaxualinfoService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwWorkspace.refreshGridData.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const baseUrls = generalConfigFromGlobal(this.gtwGlobal);
    // Pattern 8.1: globalService is the full header so JsonObjectFactoryService.getJSONObj6
    // can read globalService.globalParams and merge client_key / sso_id / jcd_key / etc.
    this.globalService = header;
    this.userSettings = header.userSettingsData || {};
    this.GENERAL_CONFIG = { ...baseUrls, ...(header.generalConfigData || {}) };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isLoading) return;

    const hasBaseUrl = !!this.GENERAL_CONFIG?.base_url;
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
    const hasGlobalParams = this.globalService?.globalParams
      && Object.keys(this.globalService.globalParams).length > 0;

    if (!hasBaseUrl || !hasFilterParams || !hasGlobalParams) return;

    const contextKey = [
      this.GENERAL_CONFIG.base_url,
      this.filterParams?.tax_year || '',
      this.filterParams?.scenario || '',
      this.filterParams?.jcd_key || '',
      this.filterParams?.filing_key || this.filterParams?.filing_group || ''
    ].join('|');

    if (contextKey === this.lastContextKey) return;

    this.lastContextKey = contextKey;

    if (this.isInitialized) {
      this.reloadData();
      return;
    }

    this.init();
  }

  private reloadData(): void {
    this.isLoading = false;
    this.loadingData = false;
    this.showLoader = true;
    this.cdr.detectChanges();
    this.tbl_list = [];
    this.sublist = [];
    this.sublist1 = [];
    this.init();
  }

  init(): void {
    this.isInitialized = true;
    this.isLoading = true;
    this.loadingData = false;
    this.showLoader = true;
    this.cdr.detectChanges();
    const baseUrl = this.GENERAL_CONFIG.base_url;
    this.service.loadForm851(baseUrl, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data?.callSuccess === '1' && data?.jsonObject) {
          const allRows: any[] = data.jsonObject || [];
          this.sublist = [];
          this.sublist1 = [];
          allRows.forEach((row: any) => {
            row.SNO = String(row.SNO ?? '');
            if (row.FIELD_TYPE === 'CHECKBOX') {
              row.ATTRIB_VALUE = row.ATTRIB_VALUE === 'N' ? 'No' : 'Yes';
            }
            if (row.FIELD_TYPE === 'TEXT' && row.LINE_NO === '(e)') {
              this.sublist.push({
                ATTRIB_VALUE: row.ATTRIB_VALUE,
                line: row.LINE_NO,
                ATTRIB_NAME: row.ATTRIB_NAME
              });
            }
            if (row.FIELD_TYPE === 'TEXT' && row.LINE_NO === '(f)') {
              this.sublist1.push({
                ATTRIB_VALUE: row.ATTRIB_VALUE,
                line: row.LINE_NO,
                ATTRIB_NAME: row.ATTRIB_NAME
              });
            }
          });
          this.tbl_list = allRows.filter((row: any) =>
            row.FIELD_TYPE === 'CHECKBOX' ||
            (row.FIELD_TYPE === 'TEXT' && row.SNO === '1')
          );
        } else if (data?.errorMessage === 'no access') {
          this.gtwSnackbarService.openSnackBar('Sorry you do not have access to do the requested action.', 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('An error has occurred. If you continue to experience this issue please visit the help page to contact support.', 'gtw-snackbar--red');
        }
        this.showLoader = false;
        this.loadingData = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      () => {
        this.showLoader = false;
        this.loadingData = false;
        this.isLoading = false;
        this.gtwSnackbarService.openSnackBar('Error loading Form 851 data.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  addE(): void {
    this.sublist.push({ LINE: '(e)', ATTRIB_VALUE: '', ATTRIB_NAME: 'F851_PIII_LINE_E' });
    this.cdr.detectChanges();
  }

  removeE(index: number): void {
    this.sublist.splice(index, 1);
    this.cdr.detectChanges();
  }

  addF(): void {
    this.sublist1.push({ LINE: '(f)', ATTRIB_VALUE: '', ATTRIB_NAME: 'F851_PIII_LINE_F' });
    this.cdr.detectChanges();
  }

  removeF(index: number): void {
    this.sublist1.splice(index, 1);
    this.cdr.detectChanges();
  }

  reset(): void {
    this.tbl_list = [];
    this.sublist = [];
    this.sublist1 = [];
    this.showLoader = true;
    this.cdr.detectChanges();
    this.isLoading = false;
    this.init();
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    if (!this.tbl_list || this.tbl_list.length === 0) return;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const saveinsertobj: any[] = [];
    this.tbl_list.forEach((row: any) => {
      if (row.FIELD_TYPE === 'CHECKBOX') {
        saveinsertobj.push({
          ATTRIB_NAME: row.ATTRIB_NAME,
          ATTRIB_VALUE: row.ATTRIB_VALUE === 'No' ? 'N' : 'Y',
          SNO: ''
        });
      }
    });
    this.sublist.forEach((item: any, i: number) => {
      saveinsertobj.push({
        ATTRIB_NAME: item.ATTRIB_NAME,
        ATTRIB_VALUE: item.ATTRIB_VALUE,
        SNO: i + 1
      });
    });
    this.sublist1.forEach((item: any, i: number) => {
      saveinsertobj.push({
        ATTRIB_NAME: item.ATTRIB_NAME,
        ATTRIB_VALUE: item.ATTRIB_VALUE,
        SNO: i + 1
      });
    });

    const globalParams = (this.globalService as any)?.globalParams || {};
    const logged_in_user = this.userSettings?.user?.sso_id ?? globalParams.sso_id ?? '';
    const saveinsertobjclob = [{
      tax_year: this.filterParams?.tax_year,
      scenario: this.filterParams?.scenario,
      jcd_key: globalParams.jcd_key,
      sso_id: logged_in_user,
      filing_group: this.filterParams?.filing_key ?? this.filterParams?.filing_group
    }];

    const baseUrl = this.GENERAL_CONFIG.base_url;
    const ssoId = logged_in_user;
    this.service.saveForm851(baseUrl, saveinsertobjclob, saveinsertobj, this.globalService, this.filterParams, ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;
        if (result?.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Data has been saved', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
          this.init();
        }
        this.cdr.detectChanges();
      },
      () => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }
}
